import React, { useContext, useEffect, useRef, useState } from "react";
import { httpGet, httpPost } from "../../components/helpers/httpMethods";
import { UserDataContext } from "../../context/userContext";
import RequestIssueModal, { periodLabel } from "../../components/Archive/RequestIssueModal";
import ArchiveCheckout from "../../components/Archive/ArchiveCheckout";
import { formatDay, todayIso } from "../../components/Archive/parseIssueQuery";
import "../../components/Archive/archive.css";
import ArchivePapers from "../../components/Archive/ArchivePapers";
import BannerSlot from "../../components/Banner/BannerSlot";

const MAX = 10;
const REQ = { requested: "Requested", notified: "Publisher notified", fulfilled: "Ready", unavailable: "Not available", cancelled: "Cancelled" };
const REQ_CLASS = { requested: "is-grey", notified: "is-orange", fulfilled: "is-green", unavailable: "is-grey", cancelled: "is-grey" };
const naira = (v) => `₦${Number(v).toLocaleString("en-NG")}`;
const longDay = (iso) => formatDay(iso, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

function describe(row) {
  switch (row.status) {
    case "owned":
      return { badge: "In your library", cls: "is-blue", note: "You bought this edition.", action: "Read", primary: true };
    case "archive":
      return { badge: "Archive", cls: "is-blue", note: `Archive edition · ${row.band}`, action: `Buy & read · ${naira(row.price)}`, primary: true };
    case "on_tns":
      return {
        badge: "On TheNewspaperStand",
        cls: "is-green",
        note: row.subscribed ? "Included in your subscription." : "Available to buy, or with a subscription.",
        action: row.subscribed ? "Read" : "Open",
        primary: Boolean(row.subscribed),
      };
    case "missing":
      if (row.requestStatus) {
        return { badge: "Not in the archive yet", cls: "is-orange", note: `You’ve requested this issue · ${REQ[row.requestStatus] || row.requestStatus}`, action: REQ[row.requestStatus] || "Requested", disabled: true };
      }
      return {
        badge: "Not in the archive yet",
        cls: "is-orange",
        note: row.requestCount
          ? `${row.requestCount} reader${row.requestCount === 1 ? " has" : "s have"} asked for this. Request it too, free.`
          : "Request it free. You only pay when it’s available.",
        action: "Request issue",
      };
    default:
      return { badge: "Not published yet", cls: "is-grey", note: "This date hasn’t happened yet.", action: null };
  }
}

export default function ArchiveHome({ history }) {
  const { userIsLogin, checkUserIsLogin } = useContext(UserDataContext);
  const [pub, setPub] = useState(null);
  const [pubQuery, setPubQuery] = useState("");
  const [pubResults, setPubResults] = useState([]);
  const [dates, setDates] = useState([]);
  const [dateInput, setDateInput] = useState("");
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mine, setMine] = useState([]);
  const [papers, setPapers] = useState([]);
  const [modal, setModal] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [browse, setBrowse] = useState(null);
  const [browseMonth, setBrowseMonth] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [undo, setUndo] = useState(null); // { ids, text }
  const undoTimer = useRef(null);
  const timer = useRef(null);

  const loadMine = async () => {
    const r = await httpGet("archive_requests/mine");
    if (r && r.data && Array.isArray(r.data.requests)) setMine(r.data.requests);
  };

  useEffect(() => {
    if (typeof checkUserIsLogin === "function") checkUserIsLogin();
    httpGet("publications").then((r) => {
      const d = r && r.data;
      const list = Array.isArray(d) ? d : d && Array.isArray(d.publications) ? d.publications : [];
      setPapers(list.filter((p) => p && p.name && p.status !== "deleted").slice(0, 10));
    });
  }, []);

  useEffect(() => {
    if (userIsLogin === true) loadMine();
  }, [userIsLogin]);

  // /archive?buy=<editionId> opens the checkout for that edition (used by the reader and for testing).
  // /archive?paper=<id>&month=YYYY-MM opens that paper's old issues at that month (link in the "ready" email).
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const buy = qs.get("buy");
    if (buy && /^[0-9a-f-]{36}$/i.test(buy)) setCheckout(buy);
    // /archive?date=YYYY-MM-DD (home page "Find that day"): the date is filled in; the reader picks the paper.
    const day = qs.get("date");
    if (/^\d{4}-\d{2}-\d{2}$/.test(day || "")) {
      setDates([day]);
      setTimeout(() => {
        const el = document.querySelector(".ar-finder");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        const pubInput = document.getElementById("ar-pub");
        if (pubInput) pubInput.focus({ preventScroll: true });
      }, 300);
    }
    const paperId = qs.get("paper");
    if (paperId && /^[0-9a-f-]{36}$/i.test(paperId)) {
      const month = qs.get("month");
      httpGet("archive_papers").then((r) => {
        const list = r && r.data && Array.isArray(r.data.papers) ? r.data.papers : [];
        const p = list.find((x) => x.id === paperId);
        if (!p) return;
        setBrowseMonth(/^\d{4}-\d{2}$/.test(month || "") ? month : null);
        setBrowse(p);
        const el = document.querySelector(".ap-section, .ap-view");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [history.location.search]);

  const cancelRequest = async (r) => {
    setCancelling(r.id);
    const res = await httpPost(`archive_requests/${r.id}/cancel`, {});
    setCancelling(null);
    setConfirmCancel(null);
    if (res && !res.er) setMine((list) => list.filter((x) => x.id !== r.id));
  };
  // Past requests (not available, or ready and already bought) can be removed; nothing is deleted on the server.
  const isPast = (r) => r.status === "unavailable" || (r.status === "fulfilled" && r.bought);
  const showUndo = (ids, text) => {
    clearTimeout(undoTimer.current);
    setUndo({ ids, text });
    undoTimer.current = setTimeout(() => setUndo(null), 7000);
  };
  const removeRequests = async (ids, all) => {
    const keep = mine;
    const gone = all ? mine.filter(isPast).map((r) => r.id) : ids;
    setMine((list) => list.filter((x) => !gone.includes(x.id)));
    const res = await httpPost("archive_requests/hide", all ? { past: true } : { ids });
    if (!res || res.er) {
      setMine(keep);
      return;
    }
    const n = gone.length;
    showUndo(gone, n === 1 ? "Request removed." : `${n} requests removed.`);
  };
  const undoRemove = async () => {
    if (!undo) return;
    const { ids } = undo;
    clearTimeout(undoTimer.current);
    setUndo(null);
    await httpPost("archive_requests/unhide", { ids });
    loadMine();
  };
  useEffect(() => () => clearTimeout(undoTimer.current), []);
  const openRequest = (r) => {
    if (r.status !== "fulfilled" || !r.editionId) return;
    if (r.span === "day") setCheckout(r.editionId);
    else history.push(`/archive?paper=${r.publicationId}&month=${r.edMonth || String(r.date).slice(0, 7)}`);
  };

  useEffect(() => {
    if (window.location.hash === "#requests") {
      const el = document.getElementById("requests");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [mine]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (pub || pubQuery.trim().length < 2) {
      setPubResults([]);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      const r = await httpGet(`search?q=${encodeURIComponent(pubQuery.trim())}&type=publications&limit=6`);
      setPubResults(r && r.data && Array.isArray(r.data.publications) ? r.data.publications : []);
    }, 250);
    return () => clearTimeout(timer.current);
  }, [pubQuery, pub]);

  const addDate = (value = dateInput) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setError("Pick a date first.");
      return dates;
    }
    if (dates.includes(value)) {
      setDateInput("");
      return dates;
    }
    if (dates.length >= MAX) {
      setError(`You can check up to ${MAX} dates at a time.`);
      return dates;
    }
    const next = [...dates, value].sort();
    setError("");
    setDates(next);
    setDateInput("");
    return next;
  };

  const runLookup = async (p, list) => {
    setBusy(true);
    setError("");
    const r = await httpGet(`archive_lookup?publicationId=${p.id}&dates=${list.join(",")}`);
    setBusy(false);
    if (!r || r.er) {
      setError((r && r.er && (r.er.message || String(r.er))) || "Something went wrong. Please try again.");
      return;
    }
    setLookup(r.data);
  };

  const find = () => {
    if (!pub) {
      setError("Choose a paper or magazine first.");
      return;
    }
    const list = dateInput ? addDate(dateInput) : dates;
    if (!list.length) {
      setError("Add at least one date.");
      return;
    }
    runLookup(pub, list);
  };

  const lookupPub = lookup && lookup.publication;
  const openModal = (datesList) =>
    setModal({ pub: lookupPub ? { id: lookupPub.id, name: lookupPub.name, logo: lookupPub.logo } : pub, dates: datesList });

  const act = (row) => {
    if (row.status === "owned" || (row.status === "on_tns" && row.subscribed)) {
      history.push(`/view_single_paper/${row.edition.id}`);
    } else if (row.status === "archive") {
      setCheckout(row.edition.id);
    } else if (row.status === "on_tns") {
      history.push(`/publication/${encodeURIComponent(lookupPub.name)}`);
    } else if (row.status === "missing" && !row.requestStatus) {
      openModal([row.date]);
    }
  };

  const onSent = () => {
    if (lookup) runLookup(lookup.publication, lookup.results.map((r) => r.date));
    loadMine();
  };

  const missingOpen = lookup ? lookup.results.filter((r) => r.status === "missing" && !r.requestStatus).map((r) => r.date) : [];

  return (
    <div className="ar-page">
      <section className="ar-hero">
        <span className="ar-eyebrow">TheNewspaperStand archive</span>
        <h1 className="ar-h1">Find any day’s paper</h1>
        <p className="ar-lead">Read past issues, or request one we don’t have yet. Requests are free, and you only pay once an issue is available.</p>
      </section>
      <BannerSlot spot="archive_top" inline />

      <section className="ar-card ar-finder" aria-label="Find issues">
        <div className="ar-finder-grid">
          <div className="ar-field">
            <label className="ar-label" htmlFor="ar-pub">Paper or magazine</label>
            {pub ? (
              <div className="ar-picked">
                <span className="ar-logo">{pub.logo ? <img src={pub.logo} alt="" /> : (pub.name || "?").charAt(0)}</span>
                <span className="ar-picked-name">{pub.name}</span>
                <button type="button" className="ar-chip-btn" onClick={() => { setPub(null); setPubQuery(""); setLookup(null); }}>Change</button>
              </div>
            ) : (
              <>
                <input id="ar-pub" className="ar-input" value={pubQuery} onChange={(e) => setPubQuery(e.target.value)} placeholder="e.g. Vanguard" autoComplete="off" />
                {pubResults.length > 0 ? (
                  <ul className="ar-options">
                    {pubResults.map((p) => (
                      <li key={p.id}>
                        <button type="button" className="ar-option" onClick={() => { setPub({ id: p.id, name: (p.name || "").trim(), logo: p.logo }); setPubResults([]); }}>
                          <span className="ar-logo ar-logo-sm">{p.logo ? <img src={p.logo} alt="" /> : (p.name || "?").charAt(0)}</span>
                          {(p.name || "").trim()}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
          </div>
          <div className="ar-field">
            <label className="ar-label" htmlFor="ar-date">Date</label>
            <div className="ar-row">
              <input id="ar-date" type="date" className="ar-input" min="1960-01-01" max={todayIso()} value={dateInput} onChange={(e) => setDateInput(e.target.value)} />
              <button type="button" className="ar-btn ar-btn-outline" onClick={() => addDate()}>+ Add</button>
            </div>
          </div>
          <button type="button" className="ar-btn" onClick={find} disabled={busy}>{busy ? "Checking…" : "Find issues"}</button>
        </div>
        {dates.length > 0 ? (
          <div className="ar-chips">
            <span className="ar-muted" style={{ fontSize: 13 }}>Dates:</span>
            {dates.map((d) => (
              <span key={d} className="ar-chip">
                {formatDay(d)}
                <button type="button" aria-label={`Remove ${formatDay(d)}`} onClick={() => setDates(dates.filter((x) => x !== d))}>✕</button>
              </span>
            ))}
          </div>
        ) : (
          <span className="ar-muted" style={{ fontSize: 13 }}>Add one or more dates, up to {MAX}.</span>
        )}
        {error ? <p className="ar-error" role="alert">{error}</p> : null}
      </section>

      <div className="ar-layout">
        <section className="ar-main" aria-live="polite">
          {lookup ? (
            <>
              <div className="ar-results-head">
                <h2 className="ar-h2">{lookup.publication.name} · {lookup.results.length} date{lookup.results.length === 1 ? "" : "s"}</h2>
                {missingOpen.length > 1 ? (
                  <button type="button" className="ar-btn ar-btn-outline ar-btn-sm" onClick={() => openModal(missingOpen)}>Request all {missingOpen.length} missing</button>
                ) : null}
              </div>
              {lookup.results.map((row) => {
                const d = describe(row);
                return (
                  <div className="ar-result" key={row.date}>
                    <span className={`ar-thumb${row.edition ? "" : " is-missing"}`}>
                      {row.edition && row.edition.image ? <img src={row.edition.image} alt="" loading="lazy" /> : null}
                    </span>
                    <div className="ar-result-body">
                      <span className="ar-result-date">{longDay(row.date)}</span>
                      <span className={`ar-badge ${d.cls}`}>{d.badge}</span>
                      <span className="ar-result-note">{d.note}</span>
                    </div>
                    {d.action ? (
                      <button type="button" className={`ar-btn ar-btn-sm${d.primary ? "" : " ar-btn-outline"}`} disabled={Boolean(d.disabled)} onClick={() => act(row)}>
                        {d.action}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </>
          ) : (
            <div className="ar-card">
              <h2 className="ar-h2">How it works</h2>
              <ol className="ar-steps" style={{ marginTop: 14 }}>
                <li className="ar-step"><span className="ar-step-n">1</span><span><strong>Choose a paper and dates</strong>Add up to {MAX} dates and press Find issues.</span></li>
                <li className="ar-step"><span className="ar-step-n">2</span><span><strong>Read what’s available</strong>Issues on TheNewspaperStand open straight away.</span></li>
                <li className="ar-step"><span className="ar-step-n">3</span><span><strong>Request the rest, free</strong>We ask the publisher and email you when it’s ready.</span></li>
              </ol>
            </div>
          )}
        </section>

        <aside className="ar-side">
          <div className="ar-card ar-reqs" id="requests">
            <h2 className="ar-h2 ar-reqs-title">Your requests</h2>
            {userIsLogin !== true ? (
              <>
                <p className="ar-result-note">Log in to request issues and track them here.</p>
                <button type="button" className="ar-btn ar-btn-outline ar-btn-sm" onClick={() => history.push("/login")}>Log in</button>
              </>
            ) : (
              (() => {
                const active = mine.filter((r) => !isPast(r));
                const past = mine.filter(isPast);
                return (
                  <>
                    {active.length ? (
                      <ul className="ar-requests">
                        {active.map((r) => (
                          <li className={`ar-request${r.status === "fulfilled" ? " is-ready" : ""}`} key={r.id}>
                            <span className="ar-request-title">{r.paper} · {periodLabel(r.span || "day", r.date)}</span>
                            <span className="ar-request-meta">
                              {r.status === "fulfilled" ? "Arrived — ready to read" : `Requested ${formatDay(String(r.createdAt).slice(0, 10), { day: "numeric", month: "short" })}`}
                              <span className={`ar-badge ${REQ_CLASS[r.status] || "is-grey"}`}>{REQ[r.status] || r.status}</span>
                            </span>
                            {confirmCancel === r.id ? (
                              <span className="ar-request-confirm" role="group" aria-label="Cancel this request?">
                                <span>Cancel this request?</span>
                                <button type="button" className="ar-link" disabled={cancelling === r.id} onClick={() => cancelRequest(r)}>{cancelling === r.id ? "Cancelling…" : "Yes, cancel"}</button>
                                <button type="button" className="ar-link ar-link-muted" onClick={() => setConfirmCancel(null)}>Keep it</button>
                              </span>
                            ) : (
                              <span className="ar-request-actions">
                                {r.status === "fulfilled" ? <button type="button" className="ar-btn ar-btn-sm" onClick={() => openRequest(r)}>{r.span === "day" ? "Buy & read" : "See the issues"}</button> : null}
                                {r.status !== "fulfilled" ? <button type="button" className="ar-link ar-link-muted" onClick={() => setConfirmCancel(r.id)}>Cancel request</button> : null}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="ar-result-note ar-reqs-empty">
                        {past.length ? "Nothing waiting right now. We’ll email you as soon as an issue you ask for arrives." : "You haven’t requested any issues yet. Can’t find a date? Ask for it, free."}
                      </p>
                    )}

                    {past.length ? (
                      <div className={`ar-past${showPast ? " is-open" : ""}`}>
                        <div className="ar-past-head">
                          <button type="button" className="ar-past-toggle" aria-expanded={showPast} aria-controls="ar-past-list" onClick={() => setShowPast((v) => !v)}>
                            <span>Past requests</span>
                            <span className="ar-past-count">{past.length}</span>
                            <svg className="ar-past-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
                          </button>
                          {showPast ? <button type="button" className="ar-link ar-link-muted" onClick={() => removeRequests([], true)}>Clear all</button> : null}
                        </div>
                        {showPast ? (
                          <>
                            <ul className="ar-past-list" id="ar-past-list">
                              {past.map((r) => (
                                <li className="ar-past-item" key={r.id}>
                                  <span className="ar-past-text">
                                    <span className="ar-past-name">{r.paper} · {periodLabel(r.span || "day", r.date)}</span>
                                    <span className="ar-past-why">
                                      {r.status === "fulfilled" ? "Bought · in your library" : r.closedReason === "publisher has no copy" ? "Publisher has no copy" : "No copy found"}
                                    </span>
                                  </span>
                                  <button type="button" className="ar-past-x" aria-label={`Remove ${r.paper} · ${periodLabel(r.span || "day", r.date)}`} title="Remove" onClick={() => removeRequests([r.id])}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                  </button>
                                </li>
                              ))}
                            </ul>
                            <p className="ar-past-note">Past requests clear themselves 30 days after they close.</p>
                          </>
                        ) : null}
                      </div>
                    ) : null}

                    {undo ? (
                      <div className="ar-undo" role="status">
                        <span>{undo.text}</span>
                        <button type="button" className="ar-link" onClick={undoRemove}>Undo</button>
                      </div>
                    ) : null}
                  </>
                );
              })()
            )}
            <div className="ar-reqs-foot">
              <button type="button" className="ar-link" onClick={() => setModal({ pub: pub || null, dates: [] })}>Request an issue</button>
              {userIsLogin === true ? <button type="button" className="ar-link" onClick={() => history.push("/account/library")}>My library</button> : null}
            </div>
          </div>
        </aside>
      </div>

      {papers.length > 0 ? (
        <ArchivePapers
            browse={browse}
            onChoose={(p) => setBrowse(p)}
            onBack={() => setBrowse(null)}
            onBuy={(id) => setCheckout(id)}
            onRequest={(p, preset) => setModal({ pub: { id: p.id, name: p.name, logo: p.logo }, dates: (preset && preset.dates) || [], span: preset && preset.span })}
            history={history}
            reloadKey={checkout}
            initialMonth={browseMonth}
          />
      ) : null}

      {checkout ? (
        <ArchiveCheckout
          editionId={checkout}
          history={history}
          onClose={() => {
            setCheckout(null);
            if (lookup) runLookup(lookup.publication, lookup.results.map((r) => r.date));
          }}
        />
      ) : null}

      {modal ? (
        <RequestIssueModal onClose={() => setModal(null)} onSent={onSent} history={history} initialPublication={modal.pub} initialDates={modal.dates} initialSpan={modal.span} />
      ) : null}
    </div>
  );
}